// =============================================
// HomePage.jsx - 홈 화면
// =============================================
// 구성 (위에서 아래로):
//   1. 주요 지수 카드 8개 (KOSPI/KOSDAQ/다우/나스닥/금/은/BTC/ETH)
//      └ 카드 클릭 → 아래에 초대형 차트
//   2. 종합 자산 위젯 (좌: 가치 변화 선 그래프 / 우: 요약 카드)
//      └ 백엔드 /api/my-assets/history 데이터를 Recharts로 시각화
//   3. 시장 현황판 탭 (MarketOverview 컴포넌트)
//
// ★ 차트 엔진 이원화:
//   - 가상화폐 (BTC, ETH):
//       → TradingViewWidget (BINANCE:BTCUSDT 형식, 실시간 스트리밍)
//   - 주식·지수 (KOSPI, KOSDAQ, 다우, 나스닥, 금, 은):
//       → YahooChart (Yahoo Finance 데이터 + lightweight-charts 렌더링)
//         티커 형식: ^KS11, ^KQ11, ^DJI, ^IXIC, GC=F, SI=F
//
// ★ 클릭 → 차트 전달 흐름:
//   ① 사용자가 IndexCard 클릭
//   ② handleIndexClick(cardDef) 실행
//   ③ cardDef.chartEngine 확인 → 'yahoo' 또는 'tradingview'
//   ④ selectedIndex 상태 업데이트 → React 리렌더링
//   ⑤ 차트 div가 화면에 나타남 → scrollIntoView로 이동
// =============================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { Link } from 'react-router-dom';
import MarketOverview from '../components/MarketOverview';
import TradingViewWidget from '../components/TradingViewWidget';
import YahooChart from '../components/YahooChart';
import { useAuth } from '../contexts/AuthContext';

const API_URL = 'http://localhost:8000';

// TradingView (코인) 봉 단위 — TradingView 형식 ('D', '60' 등)
const TV_INTERVALS = [
  { label: '15분', value: '15'  },
  { label: '1시간', value: '60'  },
  { label: '4시간', value: '240' },
  { label: '일봉',  value: 'D'   },
  { label: '주봉',  value: 'W'   },
];

// Yahoo Finance (주식·지수) 봉 단위 — yfinance 형식 ('1d', '1h' 등)
const YAHOO_INTERVALS = [
  { label: '15분', value: '15m' },
  { label: '1시간', value: '1h'  },
  { label: '일봉',  value: '1d'  },
  { label: '주봉',  value: '1w'  },
];

// 포트폴리오 위젯 기간 버튼
const PORTFOLIO_PERIODS = [
  { label: '1개월', value: '1mo' },
  { label: '3개월', value: '3mo' },
  { label: '1년',   value: '1y'  },
];

// =============================================
// ★★★ getTradingViewSymbol — 핵심 변환 함수 ★★★
// =============================================
// "어떤 종목을 클릭해도 AAPL이 나오는 문제"의 원인은
// TradingView가 모르는 심볼을 받으면 기본값(AAPL)을 보여주기 때문이야.
//
// TradingView 심볼 형식 = "거래소:심볼코드"
// ┌──────────────────────────────────────────────────────┐
// │  잘못된 예 → "KOSPI"         → TradingView 모름 → AAPL │
// │  올바른 예 → "KRX:KOSPI"     → TradingView 인식 → 차트 │
// └──────────────────────────────────────────────────────┘
//
// type별 변환 규칙:
//   'kr_index'     : KOSPI/KOSDAQ → "KRX:KOSPI" (한국 지수)
//   'kr_stock'     : "005930.KS"  → "KRX:005930" (.KS/.KQ 제거 후 KRX: 붙이기)
//   'global_index' : DJI          → "TVC:DJI" (TVC = TradingView 자체 데이터)
//   'commodity'    : GOLD         → "COMEX:GC1!" (금 선물)
//   'crypto'       : BTC          → "BINANCE:BTCUSDT"
//
// TVC(TradingView 자체 데이터)를 쓰는 이유:
//   → 거래소 의존 없이 TradingView가 직접 제공 → 심볼 에러 없음!
export function getTradingViewSymbol(type, identifier) {
  switch (type) {

    case 'kr_index': {
      // 한국 지수: TVC (TradingView 자체 데이터 — 가장 안정적)
      const MAP = { KOSPI: 'TVC:KOSPI', KOSDAQ: 'TVC:KOSDAQ' };
      return MAP[identifier] || `TVC:${identifier}`;
    }

    case 'kr_stock': {
      // 한국 주식 변환 예시:
      //   "005930.KS" → "KRX:005930"
      const code = String(identifier).split('.')[0];
      return `KRX:${code}`;
    }

    case 'global_index': {
      // 글로벌 지수
      const MAP = {
        DJI:   'TVC:DJI',
        IXIC:  'NASDAQ:IXIC',
        SP500: 'SP:SPX',
      };
      return MAP[identifier] || `TVC:${identifier}`;
    }

    case 'commodity': {
      // 상품 선물: COMEX 거래소
      const MAP = {
        GOLD:   'COMEX:GC1!',
        SILVER: 'COMEX:SI1!',
      };
      return MAP[identifier] || `COMEX:${identifier}`;
    }

    case 'us_stock': {
      // 미국 주식
      const { ticker, exchange } = identifier;
      return `${exchange}:${ticker}`;
    }

    case 'crypto': {
      // 암호화폐: Binance 거래소 USDT 페어
      return `BINANCE:${String(identifier).toUpperCase()}USDT`;
    }

    default:
      return identifier;
  }
}


// =============================================
// 지수 카드 목록 정의 (렌더링 + 차트 심볼 매핑)
// =============================================
const INDEX_CARD_DEFS = [
  // ── 한국 지수 → Yahoo Finance (^KS11, ^KQ11) ──
  { key: 'kospi',  label: 'KOSPI',       dataKey: 'kospi',  type: 'kr_index',     identifier: 'KOSPI',  prefix: '',  decimals: 2,
    chartEngine: 'yahoo', yahooTicker: '^KS11' },
  { key: 'kosdaq', label: 'KOSDAQ',      dataKey: 'kosdaq', type: 'kr_index',     identifier: 'KOSDAQ', prefix: '',  decimals: 2,
    chartEngine: 'yahoo', yahooTicker: '^KQ11' },
  // ── 글로벌 지수 → Yahoo Finance (^DJI, ^IXIC) ──
  { key: 'dji',    label: '다우 산업',    dataKey: 'dji',    type: 'global_index', identifier: 'DJI',    prefix: '',  decimals: 0,
    chartEngine: 'yahoo', yahooTicker: '^DJI'  },
  { key: 'ixic',   label: '나스닥',       dataKey: 'ixic',   type: 'global_index', identifier: 'IXIC',   prefix: '',  decimals: 0,
    chartEngine: 'yahoo', yahooTicker: '^IXIC' },
  // ── 상품 선물 → Yahoo Finance (GC=F, SI=F) ──
  { key: 'gold',   label: '금 (Gold)',    dataKey: 'gold',   type: 'commodity',    identifier: 'GOLD',   prefix: '$', decimals: 2, unit: '/oz',
    chartEngine: 'yahoo', yahooTicker: 'GC=F'  },
  { key: 'silver', label: '은 (Silver)',  dataKey: 'silver', type: 'commodity',    identifier: 'SILVER', prefix: '$', decimals: 2, unit: '/oz',
    chartEngine: 'yahoo', yahooTicker: 'SI=F'  },
  // ── 암호화폐 → TradingView (BINANCE:BTCUSDT) ──
  { key: 'btc',    label: 'BTC / USDT',  dataKey: 'btc',    type: 'crypto',       identifier: 'BTC',    prefix: '$', decimals: 0,
    chartEngine: 'tradingview' },
  { key: 'eth',    label: 'ETH / USDT',  dataKey: 'eth',    type: 'crypto',       identifier: 'ETH',    prefix: '$', decimals: 0,
    chartEngine: 'tradingview' },
];


export default function HomePage() {
  // ★ 로그인 상태 가져오기
  // isLoggedIn: true면 로그인 중, false면 비로그인
  // → 비로그인 시 /api/my-assets API 호출을 건너뛰어 401 에러를 방지해
  const { isLoggedIn } = useAuth();

  // ── 시장 지수 데이터 상태 ──
  const [indices, setIndices] = useState(null);

  // ── 주요 지수 차트 상태 ──
  // selectedIndex: null이면 닫힘
  const [selectedIndex,  setSelectedIndex]  = useState(null);
  const [tvInterval,     setTvInterval]     = useState('D');   // TradingView 봉 단위
  const [yahooInterval,  setYahooInterval]  = useState('1d');  // Yahoo 봉 단위
  const indexChartRef = useRef(null);

  // =============================================
  // ★ 종합 자산 위젯 상태
  // =============================================
  // portfolioHistory : 날짜별 포트폴리오 가치 배열 (Recharts 차트용)
  //   → [{time: "2025-01-01", value: 4880000}, ...]
  // portfolioHistPeriod : 선택된 조회 기간 (1mo / 3mo / 1y)
  // loadingPortfolio : 로딩 스피너 표시 여부
  //
  // ★ 초기값을 true로 설정하는 이유:
  //   false로 시작하면 React 첫 렌더링에서 API 호출 전에
  //   "자산 없음" 메시지가 즉시 표시돼.
  //   → true로 시작하면 로딩 중 화면을 먼저 보여주고,
  //     API 응답 후에 올바른 화면으로 전환돼!
  const [portfolioHistory,    setPortfolioHistory]    = useState([]);
  const [portfolioHistPeriod, setPortfolioHistPeriod] = useState('1mo');
  const [loadingPortfolio,    setLoadingPortfolio]    = useState(true);  // ★ true로 시작!
  const [hasAssets,           setHasAssets]           = useState(false);
  const [usdkrw,              setUsdkrw]              = useState(1350);

  // ★ usdkrwRef: 환율을 useCallback 의존성에서 분리하기 위한 ref
  // ─────────────────────────────────────────────────────────────
  // 문제: usdkrw를 fetchPortfolioWidget의 dependency에 넣으면
  //       환율이 60초마다 업데이트될 때마다 느린 히스토리 API가 다시 호출됨.
  // 해결: ref에 최신 환율을 보관하고, 함수 내부에서는 ref.current를 읽어.
  //       이렇게 하면 환율 업데이트가 불필요한 re-fetch를 유발하지 않아.
  const usdkrwRef = useRef(1350);
  useEffect(() => { usdkrwRef.current = usdkrw; }, [usdkrw]);

  // =============================================
  // ① 시장 지수 + 환율 가져오기 (1분마다 갱신)
  // =============================================
  const fetchIndices = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/market/indices`);
      setIndices(r.data);
      // 환율은 indices 응답에 포함되어 있어
      // market.py: "usdkrw": {"price": 1480, "change_pct": ...} 형태로 반환해
      if (r.data?.usdkrw?.price) setUsdkrw(r.data.usdkrw.price);
    } catch { /* 백엔드 꺼져 있으면 무시 */ }
  }, []);

  useEffect(() => {
    fetchIndices();
    const timer = setInterval(fetchIndices, 60000);
    return () => clearInterval(timer);
  }, [fetchIndices]);

  // =============================================
  // ② 종합 자산 위젯 데이터 가져오기
  // =============================================
  // 흐름:
  //   ① GET /api/my-assets   → 자산 목록이 있는지 확인
  //   ② GET /api/my-assets/history → 날짜별 원화 합산 가치 배열 수신
  //   ③ Recharts 라인 차트에 데이터 전달
  //
  // ★ portfolioHistPeriod(기간 버튼)가 바뀔 때만 재요청해.
  //   환율(usdkrwRef)은 ref로 읽어서 불필요한 re-fetch를 막아.
  const fetchPortfolioWidget = useCallback(async () => {
    // ★ 비로그인 상태면 API 호출 없이 바로 종료
    // → 로그인 안 하면 /api/my-assets가 401을 반환해서 에러가 나.
    //   그래서 isLoggedIn이 false면 API를 아예 호출하지 않아.
    if (!isLoggedIn) {
      setLoadingPortfolio(false);
      return;
    }

    setLoadingPortfolio(true);
    try {
      // ① 자산 목록 확인 — 하나라도 있으면 위젯 표시
      // ★ 백엔드 응답 구조: { "assets": [...] }
      //   assetsRes.data 자체가 배열이 아니라 객체야!
      //   → assetsRes.data.assets 로 접근해야 배열을 얻을 수 있어.
      const assetsRes = await axios.get(`${API_URL}/api/my-assets`);
      const assets = assetsRes.data?.assets || [];
      const hasAny = assets.length > 0;
      setHasAssets(hasAny);

      if (!hasAny) {
        // 자산이 없으면 히스토리 호출 불필요
        setPortfolioHistory([]);
        return;
      }

      // ② 전체 자산의 날짜별 가치 히스토리 가져오기
      //    category="" → 모든 카테고리 합산
      //    usdkrwRef.current → 가장 최근 환율 사용 (재요청 없이)
      const histRes = await axios.get(`${API_URL}/api/my-assets/history`, {
        params: {
          period:   portfolioHistPeriod,
          usdkrw:   usdkrwRef.current,   // ★ ref로 읽어서 의존성 제거
          category: '',
        },
      });
      setPortfolioHistory(histRes.data.data || []);
    } catch (e) {
      console.error('포트폴리오 위젯 로딩 실패:', e);
      // 에러가 나도 자산 없음이 아닌 로딩 실패로 처리
      setPortfolioHistory([]);
    } finally {
      setLoadingPortfolio(false);
    }
  // ★ isLoggedIn이 바뀔 때도 재실행 — 로그인/로그아웃 시 즉시 반영
  }, [portfolioHistPeriod, isLoggedIn]);

  useEffect(() => {
    fetchPortfolioWidget();
  }, [fetchPortfolioWidget]);


  // =============================================
  // ★ 주요 지수 카드 클릭 핸들러
  // =============================================
  const handleIndexClick = (cardDef) => {
    const tvSymbol = getTradingViewSymbol(cardDef.type, cardDef.identifier);

    if (selectedIndex?.key === cardDef.key) {
      setSelectedIndex(null); // 같은 카드 → 닫기
    } else {
      setSelectedIndex({
        key:         cardDef.key,
        label:       cardDef.label,
        chartEngine: cardDef.chartEngine,
        tvSymbol:    tvSymbol,
        yahooTicker: cardDef.yahooTicker,
      });
      setTimeout(() => indexChartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  };

  // =============================================
  // ★ 포트폴리오 요약 계산 (history 데이터에서 파생)
  // =============================================
  // history 배열의 마지막 값 = 오늘 총 평가금액
  // 마지막 - 그 전 = 전일 대비 변동
  //
  // 왜 history에서 계산하나?
  //   → 별도 API 호출 없이 이미 있는 데이터를 활용하기 때문이야.
  //   → history는 forward-fill 방식이라 최신 가격이 반영돼 있어.
  const len            = portfolioHistory.length;
  const todayValue     = len > 0 ? portfolioHistory[len - 1].value : null;
  const yesterdayValue = len > 1 ? portfolioHistory[len - 2].value : null;
  const dayChangePct   = (todayValue != null && yesterdayValue != null && yesterdayValue > 0)
    ? ((todayValue - yesterdayValue) / yesterdayValue * 100)
    : null;
  const dayChangeAmt   = (todayValue != null && yesterdayValue != null)
    ? (todayValue - yesterdayValue)
    : null;
  // 한국 주식 색상 관례: 상승=빨강, 하락=파랑 (미국과 반대!)
  const dayChangeColor = dayChangePct == null
    ? '#718096'
    : dayChangePct >= 0 ? '#fc8181' : '#63b3ed';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>

      {/* ===== 섹션 1: 주요 지수 카드 (8개) ===== */}
      {/* 역할: 한국 지수/코인(4개) + 글로벌/상품(4개)을 카드로 보여줘. */}
      {/*       카드 클릭 → handleIndexClick → 차트 패널 표시             */}
      <section>
        <SectionTitle>
          주요 지수
          <span style={{ fontWeight: 400, color: '#718096', marginLeft: '0.5rem', fontSize: '0.78rem' }}>
            클릭 → 차트 보기
          </span>
        </SectionTitle>

        {/* 8개 카드 그리드 */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
          gap: '0.75rem',
        }}>
          {INDEX_CARD_DEFS.map(cardDef => {
            const d          = indices?.[cardDef.dataKey];
            const isSelected = selectedIndex?.key === cardDef.key;
            return (
              <IndexCard
                key={cardDef.key}
                label={cardDef.label}
                value={d?.price}
                change={d?.change_pct}
                prefix={cardDef.prefix}
                decimals={cardDef.decimals}
                unit={cardDef.unit}
                isSelected={isSelected}
                onClick={() => handleIndexClick(cardDef)}
              />
            );
          })}
        </div>
      </section>

      {/* =============================================
          ★ 지수 카드 클릭 시 차트 패널
          → chartEngine에 따라 YahooChart 또는 TradingViewWidget
          ============================================= */}
      {selectedIndex && (() => {
        const isYahoo     = selectedIndex.chartEngine === 'yahoo';
        const intervals   = isYahoo ? YAHOO_INTERVALS : TV_INTERVALS;
        const curInterval = isYahoo ? yahooInterval   : tvInterval;
        const setInterval = isYahoo ? setYahooInterval : setTvInterval;
        const badgeTicker = isYahoo ? selectedIndex.yahooTicker : selectedIndex.tvSymbol;

        return (
          <div
            ref={indexChartRef}
            style={{
              backgroundColor: '#1a202c',
              borderRadius: '14px',
              border: `1px solid ${isYahoo ? '#38a169' : '#3182ce'}`,
              overflow: 'hidden',
            }}
          >
            {/* 차트 헤더 */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '1rem 1.25rem', flexWrap: 'wrap', gap: '0.5rem',
              borderBottom: '1px solid #2d3748',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '0.68rem', color: '#718096', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    현재 보고 있는 종목
                  </span>
                  <span style={{ fontWeight: 'bold', color: '#e2e8f0', fontSize: '1.15rem' }}>
                    {selectedIndex.label}
                  </span>
                </div>
                <span style={{
                  fontSize: '0.72rem', color: isYahoo ? '#68d391' : '#63b3ed',
                  backgroundColor: isYahoo ? '#1c3829' : '#1e2d3d',
                  padding: '3px 9px', borderRadius: '4px', fontFamily: 'monospace',
                  border: `1px solid ${isYahoo ? '#276749' : '#2c5282'}`,
                }}>
                  {badgeTicker}
                </span>
                <span style={{
                  fontSize: '0.65rem', color: isYahoo ? '#48bb78' : '#63b3ed',
                  backgroundColor: '#2d3748', padding: '2px 7px', borderRadius: '4px',
                }}>
                  {isYahoo ? '📊 Yahoo Finance' : '📈 TradingView'}
                </span>
                <div style={{ display: 'flex', gap: '0.3rem' }}>
                  {intervals.map(iv => (
                    <button key={iv.value} onClick={() => setInterval(iv.value)} style={{
                      padding: '0.25rem 0.65rem', border: 'none', borderRadius: '5px',
                      cursor: 'pointer', fontSize: '0.75rem', fontWeight: 'bold',
                      backgroundColor: curInterval === iv.value ? '#553c9a' : '#2d3748',
                      color: curInterval === iv.value ? '#e9d8fd' : '#718096',
                      transition: 'background-color 0.15s',
                    }}>
                      {iv.label}
                    </button>
                  ))}
                </div>
              </div>
              <button onClick={() => setSelectedIndex(null)} style={closeBtnStyle}>
                ✕ 닫기
              </button>
            </div>

            {/* 차트 렌더링 — chartEngine에 따라 분기 */}
            {isYahoo ? (
              <YahooChart
                key={`${selectedIndex.yahooTicker}-${yahooInterval}`}
                ticker={selectedIndex.yahooTicker}
                interval={yahooInterval}
                height={800}
              />
            ) : (
              <TradingViewWidget
                key={selectedIndex.tvSymbol}
                symbol={selectedIndex.tvSymbol}
                interval={tvInterval}
                height={800}
              />
            )}
            <p style={chartFooterStyle}>
              {isYahoo
                ? `Yahoo Finance / yfinance · ${selectedIndex.yahooTicker}`
                : `Powered by TradingView · ${selectedIndex.tvSymbol}`}
            </p>
          </div>
        );
      })()}

      {/* =============================================
          ★ 섹션 2: 종합 자산 위젯
          =============================================
          구성:
            좌측 (60%) — 포트폴리오 가치 변화 선 그래프 (Recharts)
            우측 (40%) — 요약 카드 (총 평가금액 / 전일 대비 / 24h 수익률)

          데이터 흐름:
            ① HomePage 마운트 → fetchPortfolioWidget() 호출
            ② /api/my-assets → 자산 있으면 위젯 표시
            ③ /api/my-assets/history → 날짜별 원화 합산 가치 배열
            ④ Recharts가 배열을 선 그래프로 렌더링
            ⑤ history의 마지막 두 값으로 전일 대비 변동 계산
      ============================================= */}
      <section>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <SectionTitle style={{ margin: 0 }}>
            내 자산 현황
            <span style={{ fontWeight: 400, color: '#718096', marginLeft: '0.5rem', fontSize: '0.78rem' }}>
              실시간 환율 반영
            </span>
          </SectionTitle>
          {/* 기간 선택 버튼 */}
          {hasAssets && (
            <div style={{ display: 'flex', gap: '0.3rem' }}>
              {PORTFOLIO_PERIODS.map(p => (
                <button
                  key={p.value}
                  onClick={() => setPortfolioHistPeriod(p.value)}
                  style={{
                    padding: '0.25rem 0.7rem', border: 'none', borderRadius: '5px',
                    cursor: 'pointer', fontSize: '0.75rem', fontWeight: 'bold',
                    backgroundColor: portfolioHistPeriod === p.value ? '#2c5282' : '#2d3748',
                    color: portfolioHistPeriod === p.value ? '#90cdf4' : '#718096',
                    transition: 'background-color 0.15s',
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── 비로그인 안내 ── */}
        {!isLoggedIn && !loadingPortfolio ? (
          <div style={{
            padding: '2.5rem', textAlign: 'center',
            backgroundColor: '#1a202c', borderRadius: '14px',
            border: '1px dashed #2d3748',
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>🔐</div>
            <div style={{ color: '#a0aec0', marginBottom: '1rem', fontSize: '0.95rem' }}>
              로그인하면 내 자산 현황을 여기서 바로 확인할 수 있어.
            </div>
            <Link
              to="/login"
              style={{
                display: 'inline-block',
                padding: '0.5rem 1.5rem',
                backgroundColor: '#3182ce',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontSize: '0.88rem',
                fontWeight: 'bold',
              }}
            >
              로그인하러 가기 →
            </Link>
          </div>

        ) : !hasAssets && !loadingPortfolio ? (
          /* ── 로그인 했지만 자산이 없으면 안내 메시지 ── */
          <div style={{
            padding: '2.5rem', textAlign: 'center',
            backgroundColor: '#1a202c', borderRadius: '14px',
            border: '1px dashed #2d3748',
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>📊</div>
            <div style={{ color: '#a0aec0', marginBottom: '1rem', fontSize: '0.95rem' }}>
              아직 자산을 추가하지 않았어.
            </div>
            <Link
              to="/portfolio"
              style={{
                display: 'inline-block',
                padding: '0.5rem 1.5rem',
                backgroundColor: '#3182ce',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontSize: '0.88rem',
                fontWeight: 'bold',
              }}
            >
              내 자산 추가하러 가기 →
            </Link>
          </div>

        ) : (
          /* ── 자산이 있으면 위젯 표시 ── */
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 320px',   // 좌: 차트, 우: 요약 카드
            gap: '1rem',
            backgroundColor: '#1a202c',
            borderRadius: '14px',
            padding: '1.25rem',
            minHeight: '250px',
          }}>

            {/* ── 좌측: 가치 변화 선 그래프 ── */}
            {/* ★ 연동 흐름:
                  portfolioHistory 배열 → Recharts LineChart →
                  X축: 날짜(time), Y축: 원화 합산 가치(value)          */}
            <div>
              <div style={{ fontSize: '0.75rem', color: '#718096', marginBottom: '0.5rem' }}>
                전체 자산 가치 변화 (원화 환산 기준)
              </div>
              {loadingPortfolio ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#4a5568' }}>
                  계산 중... (종목이 많을수록 오래 걸려)
                </div>
              ) : portfolioHistory.length === 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#4a5568' }}>
                  가격 데이터를 불러오지 못했어
                </div>
              ) : (
                // Recharts ResponsiveContainer → 부모 너비에 맞게 자동 조절
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={portfolioHistory} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                    {/* 배경 격자선 */}
                    <CartesianGrid strokeDasharray="3 3" stroke="#2d3748" />

                    {/* X축: 날짜 표시 (너무 빽빽하면 자동 생략) */}
                    <XAxis
                      dataKey="time"
                      tick={{ fill: '#718096', fontSize: 11 }}
                      tickLine={false}
                      axisLine={{ stroke: '#2d3748' }}
                      // 날짜 형식 단축 (YYYY-MM-DD → MM/DD)
                      tickFormatter={v => v ? v.slice(5) : ''}
                      interval="preserveStartEnd"
                    />

                    {/* Y축: 원화 금액 (단위: 만원) */}
                    <YAxis
                      tick={{ fill: '#718096', fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      // 예: 4880000 → "488만"
                      tickFormatter={v => v >= 10000 ? `${Math.round(v / 10000)}만` : v.toLocaleString()}
                      width={55}
                    />

                    {/* 툴팁: 마우스 올리면 정확한 금액 표시 */}
                    <Tooltip
                      contentStyle={{ backgroundColor: '#2d3748', border: '1px solid #4a5568', borderRadius: '8px', fontSize: '0.8rem' }}
                      labelStyle={{ color: '#a0aec0' }}
                      itemStyle={{ color: '#90cdf4' }}
                      formatter={v => [`${Number(v).toLocaleString('ko-KR')}원`, '평가금액']}
                    />

                    {/* 라인: 파란 선 그래프 */}
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#4299e1"
                      strokeWidth={2}
                      dot={false}         // 점 없이 선만 표시 (깔끔)
                      activeDot={{ r: 4, fill: '#90cdf4' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* ── 우측: 자산 요약 카드 ── */}
            {/* ★ 색상 관례 (한국 주식 스타일):
                  상승 → 빨간색 (#fc8181)
                  하락 → 파란색 (#63b3ed)
                  이것은 미국(초록/빨강)과 반대야!                        */}
            <div style={{
              display: 'flex', flexDirection: 'column', gap: '0.75rem',
              borderLeft: '1px solid #2d3748', paddingLeft: '1rem',
            }}>
              {/* 총 평가금액 */}
              <div style={{ backgroundColor: '#171e2e', borderRadius: '10px', padding: '1rem' }}>
                <div style={{ fontSize: '0.7rem', color: '#718096', marginBottom: '0.3rem', letterSpacing: '0.05em' }}>
                  총 평가금액
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#e2e8f0', lineHeight: 1.2 }}>
                  {todayValue != null
                    ? `${Math.round(todayValue).toLocaleString('ko-KR')}원`
                    : loadingPortfolio ? '계산 중...' : '-'
                  }
                </div>
                <div style={{ fontSize: '0.68rem', color: '#4a5568', marginTop: '4px' }}>
                  원화 환산 · 1 USD = {usdkrw.toLocaleString()}원
                </div>
              </div>

              {/* 전일 대비 변동 */}
              <div style={{ backgroundColor: '#171e2e', borderRadius: '10px', padding: '1rem' }}>
                <div style={{ fontSize: '0.7rem', color: '#718096', marginBottom: '0.3rem', letterSpacing: '0.05em' }}>
                  전일 대비 변동
                </div>
                {dayChangeAmt != null ? (
                  <>
                    <div style={{ fontSize: '1.3rem', fontWeight: 'bold', color: dayChangeColor, lineHeight: 1.2 }}>
                      {dayChangeAmt >= 0 ? '+' : ''}{Math.round(dayChangeAmt).toLocaleString('ko-KR')}원
                    </div>
                    <div style={{ fontSize: '0.75rem', color: dayChangeColor, marginTop: '2px' }}>
                      {dayChangePct >= 0 ? '▲' : '▼'} {Math.abs(dayChangePct).toFixed(2)}%
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: '0.85rem', color: '#4a5568' }}>
                    {loadingPortfolio ? '계산 중...' : '데이터 부족'}
                  </div>
                )}
                <div style={{ fontSize: '0.65rem', color: '#4a5568', marginTop: '4px' }}>
                  상승=빨강, 하락=파랑 (한국 관례)
                </div>
              </div>

              {/* 내 자산 페이지 바로가기 */}
              <Link
                to="/portfolio"
                style={{
                  display: 'block',
                  textAlign: 'center',
                  padding: '0.6rem',
                  backgroundColor: '#2d3748',
                  color: '#a0aec0',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  transition: 'background-color 0.15s',
                  border: '1px solid #4a5568',
                  marginTop: 'auto',
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#3d4a5c'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2d3748'}
              >
                내 자산 상세 보기 →
              </Link>
            </div>
          </div>
        )}
      </section>

      {/* ===== 섹션 3: 시장 현황판 ===== */}
      <section>
        <MarketOverview />
      </section>

    </div>
  );
}


// =============================================
// IndexCard — 지수 카드 1개
// =============================================
// isSelected: 선택된 카드면 파란 테두리 강조
// onClick: 클릭 시 차트 펼치기
function IndexCard({ label, value, change, prefix = '', decimals = 2, unit = '', isSelected = false, onClick }) {
  const isUp    = (change ?? 0) >= 0;
  const hasData = value != null;
  const color   = hasData ? (isUp ? '#48bb78' : '#fc8181') : '#4a5568';

  const fmt = (v) => {
    if (v == null) return '-';
    return prefix + Number(v).toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }) + (unit || '');
  };

  return (
    <div
      onClick={onClick}
      title={`${label} 차트 ${isSelected ? '닫기' : '보기'}`}
      style={{
        backgroundColor: isSelected ? '#1e2d3d' : '#1a202c',
        borderRadius: '12px',
        padding: '1.1rem',
        borderLeft: `4px solid ${isSelected ? '#3182ce' : color}`,
        outline: isSelected ? '2px solid #3182ce' : 'none',
        outlineOffset: '1px',
        cursor: 'pointer',
        transition: 'background-color 0.15s, transform 0.12s',
        transform: isSelected ? 'scale(1.025)' : 'scale(1)',
        userSelect: 'none',
      }}
    >
      <div style={{ color: '#718096', fontSize: '0.76rem', marginBottom: '0.35rem', fontWeight: 600 }}>
        {label}
      </div>
      <div style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#e2e8f0', marginBottom: '0.2rem' }}>
        {fmt(value)}
      </div>
      {hasData && change != null ? (
        <div style={{ color, fontSize: '0.8rem', fontWeight: 600 }}>
          {isUp ? '▲' : '▼'} {Math.abs(change).toFixed(2)}%
        </div>
      ) : (
        <div style={{ color: '#4a5568', fontSize: '0.76rem' }}>
          {hasData ? '' : '로딩 중...'}
        </div>
      )}
      {isSelected && (
        <div style={{ marginTop: '6px', fontSize: '0.65rem', color: '#63b3ed', fontWeight: 'bold' }}>
          ▼ 차트 보는 중
        </div>
      )}
    </div>
  );
}


function SectionTitle({ children, style }) {
  return (
    <h2 style={{
      margin: '0 0 0.75rem', color: '#e2e8f0',
      fontSize: '0.9rem', fontWeight: '700',
      letterSpacing: '0.08em', textTransform: 'uppercase',
      borderLeft: '3px solid #3182ce', paddingLeft: '0.6rem',
      display: 'flex', alignItems: 'center', ...style,
    }}>
      {children}
    </h2>
  );
}

function LoadingBox({ text = '불러오는 중...' }) {
  return (
    <div style={{ padding: '2rem', textAlign: 'center', color: '#4a5568', backgroundColor: '#1a202c', borderRadius: '10px' }}>
      {text}
    </div>
  );
}

// ── 스타일 상수 ──
const closeBtnStyle = {
  background: 'none', border: '1px solid #4a5568',
  color: '#718096', borderRadius: '6px', cursor: 'pointer',
  padding: '0.25rem 0.9rem', fontSize: '0.8rem',
};

const chartFooterStyle = {
  fontSize: '0.7rem', color: '#4a5568',
  margin: 0, padding: '6px 1.25rem 10px', textAlign: 'right',
};
